import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import {
  LayoutDashboard, UtensilsCrossed, ShoppingCart, CalendarRange, LineChart,
  Coffee, Salad, Cookie, Beef, Trophy, Info,
  BookOpen, Plus, Upload, Download, Pencil, Trash2,
} from 'lucide-react';
import {
  macroTargets, principles, breakfasts, lunches, dinners, snacks, shoppingList,
  cost, adherence, rotations, computeMealPrepScore, scoreCoaching,
} from '../lib/mealPlan';
import { parseMealsInput, mealsToJson, MEAL_TYPES } from '../lib/mealImport';

const SECTIONS = [
  { key: 'dashboard', label: 'Dashboard', Icon: LayoutDashboard },
  { key: 'meals', label: 'Meals', Icon: UtensilsCrossed },
  { key: 'mymeals', label: 'My Meals', Icon: BookOpen },
  { key: 'shopping', label: 'Shopping', Icon: ShoppingCart },
  { key: 'rotation', label: 'Rotation', Icon: CalendarRange },
  { key: 'tracking', label: 'Tracking', Icon: LineChart },
];

const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;

const Macros = ({ m }) => (
  <div className="flex flex-wrap gap-1.5 text-xs">
    <span className="badge-gray">{m.cal} cal</span>
    <span className="badge-primary">{m.p}g P</span>
    <span className="badge-gray">{m.c}g C</span>
    <span className="badge-gray">{m.f}g F</span>
  </div>
);

const thisMonday = () => {
  const d = new Date();
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  return d.toISOString().split('T')[0];
};

const MealPrep = ({ session }) => {
  const [section, setSection] = useState('dashboard');

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Family Macro Meal Prep</h2>
        <p className="text-sm text-gray-500">Protein-first, cook-once, family-friendly. Adjust portions, not recipes.</p>
      </div>

      {/* Sub-section nav */}
      <div className="card p-1.5 flex gap-1 overflow-x-auto">
        {SECTIONS.map(({ key, label, Icon }) => (
          <button
            key={key}
            onClick={() => setSection(key)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
              section === key ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {section === 'dashboard' && <DashboardSection />}
      {section === 'meals' && <MealsSection />}
      {section === 'mymeals' && <MyMealsSection session={session} />}
      {section === 'shopping' && <ShoppingSection />}
      {section === 'rotation' && <RotationSection />}
      {section === 'tracking' && <TrackingSection session={session} />}
    </div>
  );
};

const DashboardSection = () => (
  <div className="space-y-6">
    {/* Principles */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {principles.map((p, i) => (
        <div key={i} className="card p-5">
          <div className="h-7 w-7 rounded-full bg-primary-100 text-primary-700 text-sm font-semibold flex items-center justify-center mb-2">{i + 1}</div>
          <p className="font-semibold text-gray-900 text-sm">{p.title}</p>
          <p className="text-xs text-gray-500 mt-1">{p.text}</p>
        </div>
      ))}
    </div>

    {/* Macro targets */}
    <div className="card p-6">
      <h3 className="font-semibold text-gray-900 mb-4">Daily Macro Targets</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b border-gray-200">
              <th className="py-2 pr-4 font-medium">Person</th>
              <th className="py-2 pr-4 font-medium">Calories</th>
              <th className="py-2 pr-4 font-medium">Protein</th>
              <th className="py-2 pr-4 font-medium">Carbs</th>
              <th className="py-2 font-medium">Fat</th>
            </tr>
          </thead>
          <tbody>
            {macroTargets.map((r) => (
              <tr key={r.person} className="border-b border-gray-100">
                <td className="py-2.5 pr-4 font-medium text-gray-900">{r.person}</td>
                <td className="py-2.5 pr-4 text-gray-700">{r.calories}</td>
                <td className="py-2.5 pr-4 text-primary-700 font-medium">{r.protein}</td>
                <td className="py-2.5 pr-4 text-gray-700">{r.carbs}</td>
                <td className="py-2.5 text-gray-700">{r.fat}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>

    {/* Cost + Adherence */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="card p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Cost Control</h3>
        <div className="grid grid-cols-3 gap-3 mb-4 text-center">
          <div><p className="text-xs text-gray-500">Weekly</p><p className="text-lg font-bold text-gray-900">{fmtMoney(cost.weekly)}</p></div>
          <div><p className="text-xs text-gray-500">Per Meal</p><p className="text-lg font-bold text-gray-900">{fmtMoney(cost.perMeal)}</p></div>
          <div><p className="text-xs text-gray-500">Per Serving</p><p className="text-lg font-bold text-gray-900">{fmtMoney(cost.perServing)}</p></div>
        </div>
        <div className="space-y-2">
          {cost.reductions.map((r) => (
            <div key={r.pct} className="flex items-start gap-2 text-sm">
              <span className="badge-success shrink-0">−{r.pct}%</span>
              <span className="text-gray-600">{r.tip}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="card p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Adherence Score</h3>
        <div className="space-y-2.5">
          {adherence.scores.map((s) => (
            <div key={s.dim}>
              <div className="flex justify-between text-sm mb-0.5">
                <span className="text-gray-600">{s.dim}</span>
                <span className="font-medium text-gray-900">{s.score}/10</span>
              </div>
              <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
                <div className="h-full bg-primary-500 rounded-full" style={{ width: `${s.score * 10}%` }} />
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-4"><span className="font-medium text-gray-700">Weaknesses: </span>{adherence.weaknesses}</p>
      </div>
    </div>
  </div>
);

const Card = ({ title, Icon, children }) => (
  <div className="card p-6">
    <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">{Icon && <Icon className="h-4 w-4 text-primary-600" />}{title}</h3>
    {children}
  </div>
);

const BatchMeal = ({ meal }) => (
  <div className="rounded-lg border border-gray-200 p-4">
    <div className="flex items-center justify-between mb-2">
      <p className="font-medium text-gray-900">{meal.name}</p>
      <span className="text-xs text-gray-400">serves {meal.serves}</span>
    </div>
    <div className="grid sm:grid-cols-2 gap-4 mb-3">
      <div>
        <p className="text-xs font-medium text-gray-500 mb-1">Ingredients</p>
        <ul className="text-sm text-gray-600 list-disc list-inside space-y-0.5">
          {meal.ingredients.map((x, i) => <li key={i}>{x}</li>)}
        </ul>
      </div>
      <div>
        <p className="text-xs font-medium text-gray-500 mb-1">Prep</p>
        <ol className="text-sm text-gray-600 list-decimal list-inside space-y-0.5">
          {meal.instructions.map((x, i) => <li key={i}>{x}</li>)}
        </ol>
      </div>
    </div>
    <p className="text-xs text-gray-500 mb-1">Per serving</p>
    <Macros m={meal.macros} />
  </div>
);

const MealsSection = () => (
  <div className="space-y-6">
    <Card title="High-Protein Grab & Go Breakfasts" Icon={Coffee}>
      <div className="divide-y divide-gray-100">
        {breakfasts.map((b, i) => (
          <div key={i} className="flex items-center justify-between py-2.5 gap-3">
            <span className="text-sm text-gray-800">{b.name}</span>
            <Macros m={b} />
          </div>
        ))}
      </div>
    </Card>

    <Card title="Lunch Batch Prep" Icon={Salad}>
      <div className="space-y-4">
        <BatchMeal meal={lunches.sunday} />
        <BatchMeal meal={lunches.wednesday} />
      </div>
    </Card>

    <Card title="7-Dinner Family Rotation" Icon={Beef}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {dinners.map((d, i) => (
          <div key={i} className="rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="font-medium text-gray-900">{i + 1}. {d.name}</p>
            </div>
            <Macros m={d.macros} />
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">Ingredients</p>
                <ul className="text-sm text-gray-600 list-disc list-inside space-y-0.5">{d.ingredients.map((x, j) => <li key={j}>{x}</li>)}</ul>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">Cook</p>
                <ol className="text-sm text-gray-600 list-decimal list-inside space-y-0.5">{d.instructions.map((x, j) => <li key={j}>{x}</li>)}</ol>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-100 text-xs space-y-1">
              <p><span className="badge-primary">Husband</span> <span className="text-gray-600">{d.adjust.husband}</span></p>
              <p><span className="badge-gray">Wife</span> <span className="text-gray-600">{d.adjust.wife}</span></p>
              <p><span className="badge-warning">Kids</span> <span className="text-gray-600">{d.adjust.kids}</span></p>
            </div>
          </div>
        ))}
      </div>
    </Card>

    <Card title="Protein Snacks" Icon={Cookie}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
        {snacks.map((s, i) => (
          <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 text-sm">
            <span className="text-gray-800">{s.name}</span>
            <span className="text-gray-500">{s.cal} cal · <span className="text-primary-700 font-medium">{s.p}g P</span></span>
          </div>
        ))}
      </div>
    </Card>
  </div>
);

const ShoppingSection = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
    {Object.entries(shoppingList).map(([group, items]) => (
      <div key={group} className="card p-5">
        <h3 className="font-semibold text-gray-900 mb-3">{group}</h3>
        <ul className="space-y-1.5">
          {items.map((x, i) => (
            <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
              {x}
            </li>
          ))}
        </ul>
      </div>
    ))}
  </div>
);

const RotationSection = () => (
  <div className="space-y-6">
    <p className="text-sm text-gray-500">Year-round variety without decision fatigue: rotate breakfasts and lunches monthly, cycle 16 dinners.</p>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card title="4 Breakfast Rotations">
        <ol className="space-y-2 text-sm text-gray-700 list-decimal list-inside">{rotations.breakfast.map((x, i) => <li key={i}>{x}</li>)}</ol>
      </Card>
      <Card title="4 Lunch Rotations">
        <ol className="space-y-2 text-sm text-gray-700 list-decimal list-inside">{rotations.lunch.map((x, i) => <li key={i}>{x}</li>)}</ol>
      </Card>
    </div>
    <Card title="16 Dinner Rotations">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        {rotations.dinner.map((x, i) => (
          <div key={i} className="rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700">
            <span className="text-gray-400 mr-1">{i + 1}.</span>{x}
          </div>
        ))}
      </div>
    </Card>
  </div>
);

const emptyLog = () => ({
  week_start: thisMonday(),
  protein_pct: '', calories_pct: '', prep_completion_pct: '',
  home_cooked_dinners: '', takeout_meals: '', grocery_spend: '',
  husband_weight: '', husband_waist: '', husband_protein_avg: '', husband_workouts: '',
  wife_weight: '', wife_waist: '', wife_protein_avg: '', wife_workouts: '',
});

const num = (v) => (v === '' || v == null ? null : parseFloat(v));

const TrackingSection = ({ session }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyLog());

  useEffect(() => { fetchLogs(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLogs = async () => {
    try {
      const { data, error } = await supabase
        .from('meal_prep_logs')
        .select('*')
        .eq('user_id', session.user.id)
        .order('week_start', { ascending: false });
      if (error) throw error;
      setLogs(data || []);
    } catch (e) {
      console.error('Error fetching meal prep logs:', e);
    } finally {
      setLoading(false);
    }
  };

  const liveScore = computeMealPrepScore({
    proteinPct: num(form.protein_pct) || 0,
    caloriesPct: num(form.calories_pct) || 0,
    homeCookedDinners: num(form.home_cooked_dinners) || 0,
    prepCompletionPct: num(form.prep_completion_pct) || 0,
  });

  const saveLog = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { error } = await supabase.from('meal_prep_logs').insert({
        user_id: session.user.id,
        week_start: form.week_start,
        protein_pct: num(form.protein_pct) || 0,
        calories_pct: num(form.calories_pct) || 0,
        prep_completion_pct: num(form.prep_completion_pct) || 0,
        home_cooked_dinners: num(form.home_cooked_dinners) || 0,
        takeout_meals: num(form.takeout_meals) || 0,
        grocery_spend: num(form.grocery_spend),
        husband_weight: num(form.husband_weight), husband_waist: num(form.husband_waist),
        husband_protein_avg: num(form.husband_protein_avg), husband_workouts: num(form.husband_workouts),
        wife_weight: num(form.wife_weight), wife_waist: num(form.wife_waist),
        wife_protein_avg: num(form.wife_protein_avg), wife_workouts: num(form.wife_workouts),
      });
      if (error) throw error;
      setForm(emptyLog());
      fetchLogs();
    } catch (err) {
      console.error('Error saving log:', err);
      alert('Error saving log: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteLog = async (id) => {
    if (!window.confirm('Delete this week\'s log?')) return;
    try {
      const { error } = await supabase.from('meal_prep_logs').delete().eq('id', id);
      if (error) throw error;
      fetchLogs();
    } catch (err) {
      console.error(err);
    }
  };

  // Plain render fn (not a nested component) so inputs don't remount / lose focus.
  const field = (label, k, rest = {}) => (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <input
        className="input h-9" value={form[k]}
        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
        {...rest}
      />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Live score */}
      <div className="card p-6 flex flex-col sm:flex-row items-center gap-6">
        <div className="text-center shrink-0">
          <div className={`text-5xl font-bold ${liveScore >= 80 ? 'text-success-600' : liveScore >= 50 ? 'text-warning-600' : 'text-danger-600'}`}>{liveScore}</div>
          <p className="text-xs text-gray-500 mt-1">Meal Prep Score / 100</p>
        </div>
        <div className="flex-1">
          <p className="text-sm text-gray-700 flex items-start gap-2"><Trophy className="h-4 w-4 text-warning-500 mt-0.5 shrink-0" />{scoreCoaching(liveScore)}</p>
          <p className="text-xs text-gray-400 mt-2">Score = Protein 25% · Calories 25% · Home-cooked dinners 25% · Prep completion 25%.</p>
        </div>
      </div>

      {/* Log form */}
      <form onSubmit={saveLog} className="card p-6 space-y-5">
        <h3 className="font-semibold text-gray-900">Log This Week</h3>

        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Meal Prep Score inputs</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {field('Week of', 'week_start', { type: 'date', required: true })}
            {field('Protein %', 'protein_pct', { type: 'number', placeholder: '0–100' })}
            {field('Calories %', 'calories_pct', { type: 'number', placeholder: '0–100' })}
            {field('Home dinners (/7)', 'home_cooked_dinners', { type: 'number', placeholder: '0–7' })}
            {field('Prep done %', 'prep_completion_pct', { type: 'number', placeholder: '0–100' })}
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Family metrics</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {field('Takeout meals', 'takeout_meals', { type: 'number' })}
            {field('Grocery spend ($)', 'grocery_spend', { type: 'number' })}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Husband</p>
            <div className="grid grid-cols-2 gap-3">
              {field('Weight (lb)', 'husband_weight', { type: 'number' })}
              {field('Waist (in)', 'husband_waist', { type: 'number' })}
              {field('Protein avg (g)', 'husband_protein_avg', { type: 'number' })}
              {field('Workouts', 'husband_workouts', { type: 'number' })}
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Wife</p>
            <div className="grid grid-cols-2 gap-3">
              {field('Weight (lb)', 'wife_weight', { type: 'number' })}
              {field('Waist (in)', 'wife_waist', { type: 'number' })}
              {field('Protein avg (g)', 'wife_protein_avg', { type: 'number' })}
              {field('Workouts', 'wife_workouts', { type: 'number' })}
            </div>
          </div>
        </div>

        <button type="submit" disabled={saving} className="btn-primary">{saving ? 'Saving...' : 'Save Week'}</button>
      </form>

      {/* History */}
      <div className="card p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Weekly History</h3>
        {loading ? (
          <p className="text-sm text-gray-500">Loading...</p>
        ) : logs.length === 0 ? (
          <p className="text-sm text-gray-500">No weeks logged yet.</p>
        ) : (
          <div className="space-y-3">
            {logs.map((l) => {
              const score = computeMealPrepScore({
                proteinPct: l.protein_pct, caloriesPct: l.calories_pct,
                homeCookedDinners: l.home_cooked_dinners, prepCompletionPct: l.prep_completion_pct,
              });
              const low = l.prep_completion_pct < 80 || score < 80;
              return (
                <div key={l.id} className="rounded-lg border border-gray-200 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`h-11 w-11 rounded-lg flex items-center justify-center font-bold ${score >= 80 ? 'bg-success-100 text-success-700' : score >= 50 ? 'bg-warning-100 text-warning-700' : 'bg-danger-100 text-danger-700'}`}>{score}</div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Week of {new Date(l.week_start).toLocaleDateString()}</p>
                        <p className="text-xs text-gray-500">
                          {l.home_cooked_dinners}/7 home dinners · {l.takeout_meals || 0} takeout{l.grocery_spend != null ? ` · ${fmtMoney(l.grocery_spend)} groceries` : ''}
                        </p>
                      </div>
                    </div>
                    <button onClick={() => deleteLog(l.id)} className="text-gray-300 hover:text-danger-500 text-xl">×</button>
                  </div>
                  {low && (
                    <p className="mt-3 text-xs text-warning-800 bg-warning-50 border border-warning-200 rounded-md px-3 py-2 flex items-start gap-2">
                      <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-warning-600" />
                      Compliance under 80% — {scoreCoaching(score)}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const emptyMeal = () => ({ name: '', type: 'dinner', calories: '', protein: '', carbs: '', fat: '', ingredients: '', instructions: '', notes: '' });

const CSV_TEMPLATE = 'name,type,calories,protein,carbs,fat,ingredients,instructions,notes\n"Chicken rice bowl",lunch,520,48,55,12,"chicken; rice; broccoli","bake; assemble","batch on Sunday"\n"Protein oats",breakfast,380,32,45,9,"oats; whey; milk","mix; chill overnight",\n';

const download = (filename, text, mime = 'text/plain') => {
  const blob = new Blob([text], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const MyMealsSection = ({ session }) => {
  const [meals, setMeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyMeal());
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState('');
  const [preview, setPreview] = useState(null); // { meals, skipped, error }

  useEffect(() => { fetchMeals(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMeals = async () => {
    try {
      const { data, error } = await supabase
        .from('meals').select('*').eq('user_id', session.user.id)
        .order('type', { ascending: true }).order('name', { ascending: true });
      if (error) throw error;
      setMeals(data || []);
    } catch (e) {
      console.error('Error fetching meals:', e);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => { setEditing(null); setForm(emptyMeal()); setShowForm(true); };
  const openEdit = (m) => {
    setEditing(m);
    setForm({
      name: m.name, type: m.type || 'dinner',
      calories: m.calories?.toString() || '', protein: m.protein?.toString() || '',
      carbs: m.carbs?.toString() || '', fat: m.fat?.toString() || '',
      ingredients: m.ingredients || '', instructions: m.instructions || '', notes: m.notes || '',
    });
    setShowForm(true);
  };

  const saveMeal = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name: form.name.trim(), type: form.type,
      calories: parseFloat(form.calories) || 0, protein: parseFloat(form.protein) || 0,
      carbs: parseFloat(form.carbs) || 0, fat: parseFloat(form.fat) || 0,
      ingredients: form.ingredients || null, instructions: form.instructions || null, notes: form.notes || null,
    };
    try {
      if (editing) {
        const { error } = await supabase.from('meals').update(payload).eq('id', editing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('meals').insert({ user_id: session.user.id, ...payload });
        if (error) throw error;
      }
      setShowForm(false); fetchMeals();
    } catch (err) {
      console.error(err); alert('Error saving meal: ' + err.message);
    } finally { setSaving(false); }
  };

  const deleteMeal = async (id) => {
    if (!window.confirm('Delete this meal?')) return;
    try {
      const { error } = await supabase.from('meals').delete().eq('id', id);
      if (error) throw error;
      fetchMeals();
    } catch (err) { console.error(err); }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { setImportText(String(reader.result || '')); setPreview(null); };
    reader.readAsText(file);
  };

  const runPreview = () => setPreview(parseMealsInput(importText));

  const confirmImport = async () => {
    if (!preview?.meals?.length) return;
    setSaving(true);
    try {
      const rows = preview.meals.map((m) => ({ user_id: session.user.id, ...m }));
      const { error } = await supabase.from('meals').insert(rows);
      if (error) throw error;
      setShowImport(false); setImportText(''); setPreview(null);
      fetchMeals();
    } catch (err) {
      console.error(err); alert('Import failed: ' + err.message);
    } finally { setSaving(false); }
  };

  const exportMeals = () => {
    if (meals.length === 0) return;
    download('lahnlab-meals.json', mealsToJson(meals), 'application/json');
  };

  const shown = filter === 'all' ? meals : meals.filter((m) => m.type === filter);

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="card p-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1.5">
          {['all', ...MEAL_TYPES].map((t) => (
            <button
              key={t}
              onClick={() => setFilter(t)}
              className={`px-3 py-1.5 rounded-md text-sm border capitalize ${filter === t ? 'bg-primary-600 text-white border-primary-600' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
            >
              {t}{t !== 'all' ? '' : ` (${meals.length})`}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => { setShowImport(true); setPreview(null); }} className="btn-outline btn-sm"><Upload className="h-4 w-4 mr-1.5" /> Import</button>
          <button onClick={exportMeals} disabled={meals.length === 0} className="btn-outline btn-sm"><Download className="h-4 w-4 mr-1.5" /> Export</button>
          <button onClick={openCreate} className="btn-primary btn-sm"><Plus className="h-4 w-4 mr-1.5" /> Add Meal</button>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <p className="text-sm text-gray-500">Loading...</p>
      ) : shown.length === 0 ? (
        <div className="card p-12 text-center">
          <BookOpen className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-700 font-medium">{meals.length === 0 ? 'No custom meals yet' : 'No meals of this type'}</p>
          <p className="text-gray-500 text-sm mt-1">Add a meal or import from JSON/CSV to build your library.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {shown.map((m) => (
            <div key={m.id} className="card p-5 group">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 truncate">{m.name}</p>
                  <span className="badge-gray capitalize mt-1 inline-block">{m.type}</span>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                  <button onClick={() => openEdit(m)} className="text-gray-400 hover:text-primary-600"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => deleteMeal(m.id)} className="text-gray-400 hover:text-danger-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="mt-3"><Macros m={{ cal: m.calories, p: m.protein, c: m.carbs, f: m.fat }} /></div>
              {m.ingredients && <p className="text-xs text-gray-500 mt-3 whitespace-pre-line line-clamp-3"><span className="font-medium text-gray-600">Ingredients: </span>{m.ingredients}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full my-8">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{editing ? 'Edit Meal' : 'Add Meal'}</h3>
            </div>
            <form onSubmit={saveMeal} className="px-6 py-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input className="input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Turkey chili" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select className="input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                    {MEAL_TYPES.map((t) => <option key={t} value={t} className="capitalize">{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Calories</label>
                  <input className="input" type="number" value={form.calories} onChange={(e) => setForm({ ...form, calories: e.target.value })} placeholder="0" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Protein (g)</label>
                  <input className="input" type="number" value={form.protein} onChange={(e) => setForm({ ...form, protein: e.target.value })} placeholder="0" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Carbs (g)</label>
                  <input className="input" type="number" value={form.carbs} onChange={(e) => setForm({ ...form, carbs: e.target.value })} placeholder="0" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fat (g)</label>
                  <input className="input" type="number" value={form.fat} onChange={(e) => setForm({ ...form, fat: e.target.value })} placeholder="0" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ingredients <span className="text-gray-400 font-normal">— one per line</span></label>
                  <textarea className="input min-h-[64px] py-2" rows={2} value={form.ingredients} onChange={(e) => setForm({ ...form, ingredients: e.target.value })} />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Instructions</label>
                  <textarea className="input min-h-[64px] py-2" rows={2} value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} />
                </div>
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowForm(false)} className="btn-outline flex-1">Cancel</button>
                <button type="submit" disabled={saving} className="btn-primary flex-1">{saving ? 'Saving...' : editing ? 'Save Changes' : 'Add Meal'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import modal */}
      {showImport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full my-8">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Import Meals</h3>
              <button onClick={() => download('meals-template.csv', CSV_TEMPLATE, 'text/csv')} className="text-xs text-primary-600 hover:underline">Download CSV template</button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <p className="text-sm text-gray-500">
                Paste <span className="font-medium">JSON</span> (array of meals) or <span className="font-medium">CSV</span> with a header row, or upload a file.
                Columns: name, type, calories, protein, carbs, fat, ingredients, instructions, notes. Only <span className="font-medium">name</span> is required.
              </p>
              <input type="file" accept=".json,.csv,.txt,text/csv,application/json" onChange={onFile} className="block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border file:border-gray-300 file:bg-white file:text-sm file:font-medium hover:file:bg-gray-50" />
              <textarea
                className="input min-h-[140px] py-2 font-mono text-xs"
                rows={6}
                value={importText}
                onChange={(e) => { setImportText(e.target.value); setPreview(null); }}
                placeholder='[{"name":"Turkey chili","type":"dinner","calories":430,"protein":40}]  — or paste CSV'
              />
              {preview && (
                preview.error ? (
                  <p className="text-sm text-danger-700 bg-danger-50 border border-danger-200 rounded-md px-3 py-2">{preview.error}</p>
                ) : (
                  <p className="text-sm text-success-700 bg-success-50 border border-success-200 rounded-md px-3 py-2">
                    Ready to import <span className="font-semibold">{preview.meals.length}</span> meal{preview.meals.length !== 1 ? 's' : ''}{preview.skipped ? ` (${preview.skipped} row${preview.skipped !== 1 ? 's' : ''} skipped — no name)` : ''}.
                  </p>
                )
              )}
              <div className="flex gap-3">
                <button onClick={() => { setShowImport(false); setImportText(''); setPreview(null); }} className="btn-outline flex-1">Cancel</button>
                {preview && !preview.error ? (
                  <button onClick={confirmImport} disabled={saving} className="btn-primary flex-1">{saving ? 'Importing...' : `Import ${preview.meals.length}`}</button>
                ) : (
                  <button onClick={runPreview} className="btn-primary flex-1">Preview</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MealPrep;
